import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

// ===================== estilos (ERP sóbrio) =====================
const C = {
  bg: '#f5f6f8', card: '#fff', border: '#e2e5ea', text: '#2b2f36', muted: '#6b7280',
  accent: '#1976d2', ok: '#1a7f37', err: '#c62828', warn: '#b26a00', head: '#f0f2f5',
};
const st = {
  page: { padding: 24, background: C.bg, minHeight: '100%', color: C.text, fontFamily: 'Inter, Roboto, "Open Sans", sans-serif' },
  h1: { fontSize: 20, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 },
  sub: { color: C.muted, fontSize: 13, marginTop: 4 },
  tabs: { display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}`, margin: '18px 0 0' },
  tab: (a) => ({ padding: '10px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 600,
    color: a ? C.accent : C.muted, borderBottom: a ? `2px solid ${C.accent}` : '2px solid transparent' }),
  card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 20, marginTop: 16 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 },
  label: { fontSize: 12, fontWeight: 600, color: C.muted, display: 'block', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff' },
  btn: (v = 'primary') => ({ padding: '9px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
    color: v === 'ghost' ? C.accent : '#fff', background: v === 'ghost' ? 'transparent' : v === 'sec' ? '#5b6472' : C.accent,
    boxShadow: v === 'ghost' ? `inset 0 0 0 1px ${C.border}` : 'none' }),
  btnMini: { padding: '5px 10px', borderRadius: 5, border: `1px solid ${C.border}`, background: '#fff',
    color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: C.text, margin: '4px 0 10px', textTransform: 'uppercase', letterSpacing: .3 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', background: C.head, borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.muted, whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: `1px solid ${C.border}` },
  banner: (kind) => ({ padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 14,
    background: kind === 'ok' ? '#e7f4ea' : kind === 'err' ? '#fdeaea' : '#fff7e6',
    color: kind === 'ok' ? C.ok : kind === 'err' ? C.err : C.warn,
    border: `1px solid ${kind === 'ok' ? '#bfe3c6' : kind === 'err' ? '#f3c0c0' : '#f0d9a8'}` }),
  pre: { background: '#0f172a', color: '#e2e8f0', padding: 14, borderRadius: 6, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 16 },
  tile: (cor) => ({ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${cor}`,
    borderRadius: 8, padding: '14px 16px' }),
  tileNum: (cor) => ({ fontSize: 24, fontWeight: 700, color: cor, lineHeight: 1.1 }),
  tileLabel: { fontSize: 12, color: C.muted, marginTop: 4 },
};

// Situação do atendimento perante o IPSEMG.
// Não há 'aguardando execução': autorizar já fatura. 'autorizado' fica só por compatibilidade
// com registros gravados antes de 29/07/2026, quando o módulo achava que eram etapas separadas.
const SITUACAO = {
  pendente:   { label: 'Não enviado',        cor: C.muted, bg: '#f1f3f5' },
  executado:  { label: 'Autorizado/faturado', cor: C.ok,    bg: '#e7f4ea' },
  parcial:    { label: 'Parcial',             cor: C.warn,  bg: '#fff7e6' },
  cancelado:  { label: 'Cancelado',           cor: C.muted, bg: '#eceff1' },
  negado:     { label: 'Não autorizado',      cor: C.err,   bg: '#fdeaea' },
  autorizado: { label: 'Autorizado',          cor: C.ok,    bg: '#e7f4ea' },
};

const LOCAIS = [
  { v: '', label: 'Todas as unidades' },
  { v: '01', label: 'Matriz (01)' },
  { v: '02', label: 'Itapecerica (02)' },
  { v: '04', label: 'Pedra do Indaiá (04)' },
  { v: 'CA', label: 'Camacho (CA)' },
];

const hoje = () => new Date().toISOString().slice(0, 10);
const diasAtras = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const fmtData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');
const fmtDataHora = (d) => (d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

export default function IpsemgTes() {
  const [tab, setTab] = useState('atendimentos');
  return (
    <div style={st.page}>
      <div>
        <h1 style={st.h1}>🏥 IPSEMG — Autorização de exames</h1>
        <div style={st.sub}>Autorização e faturamento dos exames do IPSEMG no SAFe. Atenção: no IPSEMG a autorização já é a execução — autorizar fatura.</div>
      </div>
      <div style={st.tabs}>
        <div style={st.tab(tab === 'atendimentos')} onClick={() => setTab('atendimentos')}>Atendimentos</div>
        <div style={st.tab(tab === 'log')} onClick={() => setTab('log')}>Log de transações</div>
        <div style={st.tab(tab === 'config')} onClick={() => setTab('config')}>Configuração</div>
      </div>
      {tab === 'atendimentos' && <TabAtendimentos />}
      {tab === 'log' && <TabLog />}
      {tab === 'config' && <TabConfig />}
    </div>
  );
}

// ===================== Atendimentos (tela principal da atendente) =====================
function TabAtendimentos() {
  const [filtros, setFiltros] = useState({
    dataInicio: hoje(), dataFim: hoje(), localId: '', paciente: '', protocolo: '', carteirinha: '',
  });
  const [linhas, setLinhas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [expandida, setExpandida] = useState(null);   // "LOCAL/PROTOCOLO"
  const [resultado, setResultado] = useState({});      // chave → resposta da última operação
  const [detalhe, setDetalhe] = useState({});          // chave → estágio + autorizações + divergência
  const [consultando, setConsultando] = useState(null); // "chave:operacao" em andamento

  const buscar = useCallback(async (f) => {
    setLoading(true); setErro(null);
    try {
      const r = await axios.get(`${API_URL}/ipsemg/atendimentos`, { params: { ...f, limit: 200 } });
      setLinhas(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErro(e.response?.data?.detalhes || e.response?.data?.error || 'Não foi possível carregar os atendimentos.');
      setLinhas([]);
    } finally { setLoading(false); }
  }, []);

  // Já abre com o movimento do dia — a atendente não precisa clicar em nada para ver
  useEffect(() => { buscar({ dataInicio: hoje(), dataFim: hoje() }); }, [buscar]);

  const set = (k, v) => setFiltros(f => ({ ...f, [k]: v }));
  const aplicar = () => buscar(filtros);
  const periodoRapido = (ini, fim) => { const f = { ...filtros, dataInicio: ini, dataFim: fim }; setFiltros(f); buscar(f); };
  const onEnter = (e) => { if (e.key === 'Enter') aplicar(); };

  const totais = useMemo(() => {
    const t = { total: linhas.length, executado: 0, parcial: 0, negado: 0, cancelado: 0, pendente: 0 };
    for (const l of linhas) t[l.situacao] = (t[l.situacao] || 0) + 1;
    return t;
  }, [linhas]);

  const chaveDe = (l) => `${(l.LOCAL || '').trim()}/${l.PROTOCOLO}`;

  // Depois de qualquer ação, relê o protocolo para pegar estágio/divergência do servidor
  const recarregarProtocolo = async (linha) => {
    const local = (linha.LOCAL || '').trim();
    const chave = chaveDe(linha);
    try {
      const { data } = await axios.get(`${API_URL}/ipsemg/protocolo/${local}/${linha.PROTOCOLO}`);
      setDetalhe(m => ({ ...m, [chave]: data }));
      const c = data.autorizacoes || [];
      const exec = c.filter(a => a.status === 'executado').length;
      const canc = c.filter(a => a.status === 'cancelado').length;
      const ativos = c.length - canc;
      const situacao = !c.length ? 'pendente'
        : exec > 0 ? (exec === ativos ? 'executado' : 'parcial')
          : canc === c.length ? 'cancelado' : 'negado';
      setLinhas(ls => ls.map(l => (chaveDe(l) === chave
        ? { ...l, situacao, consulta: { consultados: c.length, autorizados: exec, executados: exec, cancelados: canc, ultima_consulta: new Date().toISOString() } }
        : l)));
    } catch { /* mantém o que já está na tela */ }
  };

  // Uma única função para as 3 operações do ciclo
  const operar = async (linha, op, { dry = false, forcar = false } = {}) => {
    const local = (linha.LOCAL || '').trim();
    const chave = chaveDe(linha);
    const rotas = { consultar: 'consultar', executar: 'executar', cancelar: 'cancelar' };
    setConsultando(`${chave}:${op}`); setExpandida(chave);
    try {
      const params = {};
      if (dry) params.dryRun = 1;
      if (forcar) params.forcar = 1;
      const r = await axios.post(`${API_URL}/ipsemg/${rotas[op]}/${local}/${linha.PROTOCOLO}`, {}, { params });
      setResultado(m => ({ ...m, [chave]: { ...r.data, _op: op } }));
      if (!dry) await recarregarProtocolo(linha);
    } catch (e) {
      setResultado(m => ({ ...m, [chave]: { ...(e.response?.data || { error: `Erro ao ${op}` }), _op: op, _status: e.response?.status } }));
      if (!dry) await recarregarProtocolo(linha);
    } finally { setConsultando(null); }
  };

  const abrirDetalhe = async (linha) => {
    const chave = chaveDe(linha);
    if (expandida === chave) { setExpandida(null); return; }
    setExpandida(chave);
    if (!detalhe[chave]) await recarregarProtocolo(linha);
  };

  return (
    <>
      {/* Filtros — dia corrente por padrão, alteráveis */}
      <div style={{ ...st.card, paddingBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ width: 150 }}>
            <label style={st.label}>De</label>
            <input type="date" style={st.input} value={filtros.dataInicio} onChange={e => set('dataInicio', e.target.value)} />
          </div>
          <div style={{ width: 150 }}>
            <label style={st.label}>Até</label>
            <input type="date" style={st.input} value={filtros.dataFim} onChange={e => set('dataFim', e.target.value)} />
          </div>
          <div style={{ width: 190 }}>
            <label style={st.label}>Unidade</label>
            <select style={st.input} value={filtros.localId} onChange={e => set('localId', e.target.value)}>
              {LOCAIS.map(l => <option key={l.v} value={l.v}>{l.label}</option>)}
            </select>
          </div>
          <div style={{ width: 200 }}>
            <label style={st.label}>Paciente</label>
            <input style={st.input} value={filtros.paciente} onChange={e => set('paciente', e.target.value)}
              onKeyDown={onEnter} placeholder="nome do paciente" />
          </div>
          <div style={{ width: 130 }}>
            <label style={st.label}>Protocolo</label>
            <input style={st.input} value={filtros.protocolo} onChange={e => set('protocolo', e.target.value)}
              onKeyDown={onEnter} placeholder="270086" />
          </div>
          <div style={{ width: 170 }}>
            <label style={st.label}>Carteirinha</label>
            <input style={st.input} value={filtros.carteirinha} onChange={e => set('carteirinha', e.target.value)}
              onKeyDown={onEnter} placeholder="nº da carteira" />
          </div>
          <button style={st.btn()} onClick={aplicar} disabled={loading}>{loading ? 'Buscando…' : 'Buscar'}</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <span style={{ ...st.label, marginBottom: 0 }}>Período:</span>
          <button style={st.btnMini} onClick={() => periodoRapido(hoje(), hoje())}>Hoje</button>
          <button style={st.btnMini} onClick={() => periodoRapido(diasAtras(1), diasAtras(1))}>Ontem</button>
          <button style={st.btnMini} onClick={() => periodoRapido(diasAtras(7), hoje())}>Últimos 7 dias</button>
          <button style={st.btnMini} onClick={() => periodoRapido(diasAtras(30), hoje())}>Últimos 30 dias</button>
        </div>
      </div>

      {/* Resumo do período */}
      <div style={st.tiles}>
        <div style={st.tile(C.accent)}>
          <div style={st.tileNum(C.accent)}>{totais.total}</div>
          <div style={st.tileLabel}>Atendimentos no período</div>
        </div>
        <div style={st.tile(C.ok)}>
          <div style={st.tileNum(C.ok)}>{totais.executado}</div>
          <div style={st.tileLabel}>Autorizados e faturados</div>
        </div>
        <div style={st.tile(C.warn)}>
          <div style={st.tileNum(C.warn)}>{totais.parcial}</div>
          <div style={st.tileLabel}>Parcialmente faturados</div>
        </div>
        <div style={st.tile(C.err)}>
          <div style={st.tileNum(C.err)}>{totais.negado}</div>
          <div style={st.tileLabel}>Não autorizados</div>
        </div>
        <div style={st.tile(C.muted)}>
          <div style={st.tileNum(C.muted)}>{totais.pendente}</div>
          <div style={st.tileLabel}>Não enviados</div>
        </div>
      </div>

      {/* Lista */}
      <div style={st.card}>
        {erro && <div style={st.banner('err')}>{erro}</div>}
        {loading ? (
          <div style={{ ...st.sub, padding: '20px 0' }}>Carregando atendimentos…</div>
        ) : linhas.length === 0 ? (
          <div style={{ ...st.sub, padding: '20px 0' }}>
            Nenhum atendimento do IPSEMG no período selecionado.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={st.table}>
              <thead><tr>
                <th style={st.th}>Data</th>
                <th style={st.th}>Unidade</th>
                <th style={st.th}>Protocolo</th>
                <th style={st.th}>Paciente</th>
                <th style={st.th}>Convênio</th>
                <th style={st.th}>Carteirinha</th>
                <th style={st.th}>Situação</th>
                <th style={st.th}>Último envio</th>
                <th style={st.th}></th>
              </tr></thead>
              <tbody>
                {linhas.map((l) => {
                  const local = (l.LOCAL || '').trim();
                  const chave = `${local}/${l.PROTOCOLO}`;
                  const sit = SITUACAO[l.situacao] || SITUACAO.pendente;
                  const aberta = expandida === chave;
                  return (
                    <React.Fragment key={chave}>
                      <tr style={aberta ? { background: '#f8fafc' } : undefined}>
                        <td style={st.td}>{fmtData(l.DATA)}</td>
                        <td style={st.td}>{local}</td>
                        <td style={{ ...st.td, fontFamily: 'monospace' }}>{l.PROTOCOLO}</td>
                        <td style={st.td}>{(l.PACIENTE_NOME || '').trim()}</td>
                        <td style={{ ...st.td, fontSize: 12, color: C.muted }}>{(l.CONVENIO_ID || '').trim() || '—'}</td>
                        <td style={{ ...st.td, fontFamily: 'monospace' }}>{(l.CARTEIRINHA || '').trim() || '—'}</td>
                        <td style={st.td}>
                          <span style={{ background: sit.bg, color: sit.cor, padding: '3px 9px', borderRadius: 11, fontSize: 12, fontWeight: 600 }}>
                            {sit.label}
                          </span>
                          {l.consulta && l.consulta.consultados > 0 && (
                            <span style={{ color: C.muted, fontSize: 12, marginLeft: 6 }}>
                              {l.consulta.autorizados}/{l.consulta.consultados}
                            </span>
                          )}
                        </td>
                        <td style={{ ...st.td, color: C.muted }}>{fmtDataHora(l.consulta?.ultima_consulta)}</td>
                        <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                          <AcoesCiclo
                            linha={l} chave={chave} situacao={l.situacao} consultando={consultando}
                            aberta={aberta} temResultado={!!resultado[chave]}
                            onOperar={(op, opts) => operar(l, op, opts)}
                            onDetalhe={() => abrirDetalhe(l)}
                          />
                        </td>
                      </tr>
                      {aberta && (
                        <tr>
                          <td style={{ ...st.td, background: '#f8fafc' }} colSpan={9}>
                            {detalhe[chave]?.estagio && (
                              <EstagioDoDado
                                estagio={detalhe[chave].estagio}
                                comparacao={detalhe[chave].comparacao}
                                autorizacoes={detalhe[chave].autorizacoes}
                              />
                            )}
                            {consultando?.startsWith(chave)
                              ? <div style={st.sub}>Falando com o SAFe…</div>
                              : resultado[chave]
                                ? <ResultadoOperacao res={resultado[chave]} onSimular={() => operar(l, 'consultar', { dry: true })}
                                    onForcar={() => operar(l, 'cancelar', { forcar: true })} />
                                : <div style={st.sub}>Nenhuma operação feita nesta sessão. O quadro acima mostra o estado gravado.</div>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// Botões do ciclo, conforme a situação: consultar → executar → cancelar.
// Executar só aparece com autorização válida; cancelar some quando não há o que desfazer.
function AcoesCiclo({ linha, chave, situacao, consultando, aberta, temResultado, onOperar, onDetalhe }) {
  const ocupado = (op) => consultando === `${chave}:${op}`;
  const qualquerOcupado = !!consultando && consultando.startsWith(chave);
  const paciente = (linha.PACIENTE_NOME || '').trim();
  const podeCancelar = ['executado', 'parcial'].includes(situacao);
  const jaFaturado = podeCancelar;

  const confirmar = (op) => {
    if (op === 'consultar') {
      return window.confirm(
        `Autorizar e faturar os exames de ${paciente} no IPSEMG?\n\n` +
        'No IPSEMG a autorização JÁ É a execução: os exames entram no faturamento agora. ' +
        'Para desfazer depois, só cancelando a autorização.' +
        (jaFaturado ? '\n\nEste atendimento já tem exames faturados — envie de novo só se souber o que está fazendo.' : '')
      );
    }
    if (op === 'cancelar') {
      return window.confirm(
        `Cancelar as autorizações de ${paciente} no IPSEMG?\n\n` +
        'Isso DESFAZ o faturamento desses exames no SAFe.'
      );
    }
    return true;
  };

  const acionar = (op, opts) => { if (confirmar(op)) onOperar(op, opts); };

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <button style={{ ...st.btnMini, color: C.ok, borderColor: C.ok }}
        onClick={() => acionar('consultar')} disabled={qualquerOcupado}>
        {ocupado('consultar') ? 'Enviando…' : jaFaturado ? 'Faturar de novo' : 'Autorizar e faturar'}
      </button>
      {podeCancelar && (
        <button style={{ ...st.btnMini, color: C.err, borderColor: C.err }}
          onClick={() => acionar('cancelar', { forcar: true })} disabled={qualquerOcupado}>
          {ocupado('cancelar') ? 'Cancelando…' : 'Cancelar faturamento'}
        </button>
      )}
      <button style={st.btnMini} onClick={onDetalhe} disabled={qualquerOcupado}>
        {aberta ? 'Fechar' : temResultado ? 'Ver' : 'Detalhes'}
      </button>
    </div>
  );
}

// Mesmo bloco do módulo TISS: de onde vem o dado, até onde dá para mexer e o que
// divergiu entre o que foi enviado ao SAFe e o que está no sistema hoje.
function EstagioDoDado({ estagio, comparacao, autorizacoes = [] }) {
  const [aberto, setAberto] = useState(false);
  const divergente = comparacao?.divergente;
  const cores = divergente
    ? { bg: '#fdeaea', borda: C.err, titulo: C.err }
    : estagio.pode_alterar
      ? { bg: '#f1f8e9', borda: '#558b2f', titulo: '#33691e' }
      : { bg: '#eef2f7', borda: '#37474f', titulo: '#263238' };
  const fmt = (d) => (d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

  return (
    <div style={{ background: cores.bg, borderLeft: `4px solid ${cores.borda}`, borderRadius: 6, padding: '12px 16px', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: cores.titulo, marginBottom: 4 }}>
            {divergente ? 'Atenção: o sistema mudou depois do envio' : estagio.titulo}
          </div>
          <div style={{ fontSize: 13, color: '#455a64', lineHeight: 1.5 }}>
            {divergente
              ? `Foram enviados ${comparacao.total_enviados} exame(s) ao IPSEMG em ${fmt(comparacao.congelado_em)}. O sistema do laboratório hoje tem ${comparacao.total_atuais}. O que vale no SAFe é o que foi enviado — a diferença abaixo não chegou lá.`
              : estagio.descricao}
          </div>
        </div>
        <button style={st.btnMini} onClick={() => setAberto(!aberto)}>
          {aberto ? 'Fechar' : 'De onde vêm os dados'}
        </button>
      </div>

      {divergente && (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          {comparacao.removidos.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.err, marginBottom: 4 }}>
                Foi enviado, mas não está mais no sistema ({comparacao.removidos.length})
              </div>
              {comparacao.removidos.map((p) => (
                <div key={p.codigo} style={{ fontSize: 12, color: '#455a64' }}>
                  <span style={{ fontFamily: 'monospace' }}>{p.codigo}</span> — {p.descricao || 'sem descrição'} <em>({p.status})</em>
                </div>
              ))}
            </div>
          )}
          {comparacao.adicionados.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.warn, marginBottom: 4 }}>
                Está no sistema, mas não foi enviado ({comparacao.adicionados.length})
              </div>
              {comparacao.adicionados.map((p) => (
                <div key={p.codigo} style={{ fontSize: 12, color: '#455a64' }}>
                  <span style={{ fontFamily: 'monospace' }}>{p.codigo}</span> — {p.descricao || 'sem descrição'}
                </div>
              ))}
              <div style={{ fontSize: 11, color: '#78909c', marginTop: 4 }}>
                Consulte de novo para autorizar estes — o que já foi executado não muda.
              </div>
            </div>
          )}
        </div>
      )}

      {autorizacoes.length > 0 && (
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <table style={st.table}>
            <thead><tr>
              <th style={st.th}>Código</th><th style={st.th}>Exame</th><th style={st.th}>Situação</th>
              <th style={st.th}>Autorização</th><th style={st.th}>Executado em</th><th style={st.th}>Motivo</th>
            </tr></thead>
            <tbody>
              {autorizacoes.map((a) => {
                const sit = SITUACAO[a.status] || SITUACAO.pendente;
                return (
                  <tr key={a.id}>
                    <td style={{ ...st.td, fontFamily: 'monospace' }}>{a.codigo_procedimento}</td>
                    <td style={st.td}>{a.descricao_procedimento || '—'}</td>
                    <td style={st.td}>
                      <span style={{ background: sit.bg, color: sit.cor, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{sit.label}</span>
                    </td>
                    <td style={{ ...st.td, fontFamily: 'monospace' }}>{a.senha_autorizacao || '—'}</td>
                    <td style={{ ...st.td, color: C.muted }}>{a.executado_em ? fmt(a.executado_em) : '—'}</td>
                    <td style={{ ...st.td, color: C.muted }}>
                      {a.codigo_glosa && a.codigo_glosa !== 'A-0' ? `${a.codigo_glosa} ` : ''}
                      {a.ultimo_erro || (a.codigo_glosa === 'A-0' ? '—' : a.descricao_glosa || '—')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {aberto && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.08)', fontSize: 12, color: '#455a64', lineHeight: 1.6 }}>
          <div><strong>Paciente, exames e valores</strong> vêm do sistema do laboratório e são lidos <strong>ao vivo</strong>. Se alterarem lá, muda aqui.</div>
          <div><strong>Autorização, retorno do SAFe e a URL transmitida</strong> ficam guardados no módulo IPSEMG e <strong>não mudam</strong> — é o registro do que o IPSEMG recebeu.</div>
          <div style={{ marginTop: 6 }}>
            <strong>Até quando dá para alterar:</strong> enquanto está apenas autorizado, cancelar e consultar de novo resolve.
            Depois de <strong>executado</strong>, o procedimento entrou no faturamento do IPSEMG: mexer no sistema não altera nada lá,
            e desfazer exige cancelar a autorização no próprio SAFe.
          </div>
        </div>
      )}
    </div>
  );
}

function ResultadoOperacao({ res, onSimular, onForcar }) {
  const op = res._op || 'consultar';
  if (res.error) {
    const precisaForcar = /forcar=1/.test(res.detalhes || '');
    return (
      <div>
        <div style={st.banner('err')}>
          {res.error}
          {res.detalhes ? <div style={{ marginTop: 4, fontWeight: 400 }}>{res.detalhes}</div> : null}
        </div>
        {precisaForcar && onForcar && (
          <button style={{ ...st.btnMini, color: C.err, borderColor: C.err }} onClick={onForcar}>
            Cancelar mesmo assim (desfaz o faturamento)
          </button>
        )}
        {op === 'consultar' && onSimular && (
          <button style={{ ...st.btnMini, marginLeft: 6 }} onClick={onSimular}>Ver o que seria enviado</button>
        )}
      </div>
    );
  }
  if (res.dryRun) return <ResultadoConsulta res={res} onSimular={null} />;

  if (op === 'executar' || op === 'cancelar') {
    const feitos = op === 'executar' ? res.executados : res.cancelados;
    const kind = feitos === res.total ? 'ok' : feitos > 0 ? 'warn' : 'err';
    return (
      <div>
        <div style={st.banner(kind)}>
          <b>{feitos}/{res.total}</b> exame(s) {op === 'executar' ? 'executado(s)' : 'cancelado(s)'} no IPSEMG.
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={st.table}>
            <thead><tr>
              <th style={st.th}>Código</th><th style={st.th}>Exame</th><th style={st.th}>Autorização</th>
              <th style={st.th}>Resultado</th><th style={st.th}>Motivo</th>
            </tr></thead>
            <tbody>
              {(res.resultados || []).map((x, i) => {
                const ok = op === 'executar' ? x.executado : x.cancelado;
                return (
                  <tr key={i}>
                    <td style={{ ...st.td, fontFamily: 'monospace' }}>{x.codigo}</td>
                    <td style={st.td}>{x.descricao}</td>
                    <td style={{ ...st.td, fontFamily: 'monospace' }}>{x.autorizacao || '—'}</td>
                    <td style={{ ...st.td, color: ok ? C.ok : C.err, fontWeight: 600 }}>{ok ? '✓ OK' : '✗ Falhou'}</td>
                    <td style={{ ...st.td, color: C.muted }}>
                      {x.codigoGlosa ? `${x.codigoGlosa} ` : ''}{x.descricaoGlosa || (ok ? '—' : '')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return <ResultadoConsulta res={res} onSimular={onSimular} />;
}

function ResultadoConsulta({ res, onSimular }) {
  if (res.error) {
    return (
      <div>
        <div style={st.banner('err')}>
          {res.error}
          {res.validacao?.problemas?.length ? ` (${res.validacao.problemas.length} exame(s) sem código TUSS)` : ''}
        </div>
        {onSimular && <button style={st.btnMini} onClick={onSimular}>Ver o que seria enviado (simulação)</button>}
      </div>
    );
  }
  if (res.dryRun) {
    return (
      <div>
        <div style={st.banner('warn')}>
          Simulação — nada foi enviado ao IPSEMG. Paciente: <b>{res.paciente}</b> · {res.total} exame(s).
        </div>
        {(res.procedimentos || []).map((p, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{ ...st.label, color: C.text }}>{p.codigo} — {p.descricao}</div>
            <pre style={st.pre}>{p.url}</pre>
          </div>
        ))}
      </div>
    );
  }
  const rows = res.resultados || [];
  const ok = (res.autorizados || 0) > 0;
  return (
    <div>
      <div style={st.banner(res.autorizados === res.total ? 'ok' : ok ? 'warn' : 'err')}>
        {res.paciente} · <b>{res.autorizados}/{res.total}</b> exame(s) autorizado(s)
        {res.negados ? ` · ${res.negados} não autorizado(s)` : ''}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={st.table}>
          <thead><tr>
            <th style={st.th}>Código</th><th style={st.th}>Exame</th><th style={st.th}>Situação</th>
            <th style={st.th}>Autorização</th><th style={st.th}>Motivo</th>
          </tr></thead>
          <tbody>
            {rows.map((x, i) => (
              <tr key={i}>
                <td style={{ ...st.td, fontFamily: 'monospace' }}>{x.codigo}</td>
                <td style={st.td}>{x.descricao}</td>
                <td style={{ ...st.td, color: x.autorizado ? C.ok : C.err, fontWeight: 600 }}>
                  {x.autorizado ? '✓ Autorizado' : '✗ Não autorizado'}
                </td>
                <td style={{ ...st.td, fontFamily: 'monospace' }}>{x.autorizacao || '—'}</td>
                <td style={{ ...st.td, color: C.muted }}>
                  {x.codigoGlosa ? `${x.codigoGlosa} ` : ''}{x.descricaoGlosa || (x.autorizado ? '—' : '')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {onSimular && (
        <div style={{ marginTop: 10 }}>
          <button style={st.btnMini} onClick={onSimular}>Ver o que foi enviado (simulação)</button>
        </div>
      )}
    </div>
  );
}

// ===================== Log =====================
function TabLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const carregar = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/ipsemg/transacoes`, { params: { limit: 50 } }); setRows(r.data?.data || []); }
    catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div style={st.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={st.sectionTitle}>Últimas transações TES</div>
        <button style={st.btn('ghost')} onClick={carregar} disabled={loading}>{loading ? '…' : 'Atualizar'}</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={st.table}>
          <thead><tr>
            <th style={st.th}>Início</th><th style={st.th}>Paciente</th><th style={st.th}>Protocolo</th>
            <th style={st.th}>Exame</th><th style={st.th}>Status</th><th style={st.th}>ms</th><th style={st.th}>Motivo</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td style={st.td} colSpan={7}>Nenhuma transação registrada.</td></tr> :
              rows.map(t => (
                <tr key={t.id}>
                  <td style={st.td}>{t.inicio ? new Date(t.inicio).toLocaleString('pt-BR') : '—'}</td>
                  <td style={st.td}>{t.contexto?.paciente || '—'}</td>
                  <td style={{ ...st.td, fontFamily: 'monospace' }}>
                    {t.contexto?.protocolo ? `${t.contexto.local || ''}/${t.contexto.protocolo}` : '—'}
                  </td>
                  <td style={{ ...st.td, fontFamily: 'monospace' }}>{t.contexto?.procedimento || '—'}</td>
                  <td style={{ ...st.td, color: t.sucesso ? C.ok : C.err, fontWeight: 600 }}>
                    {t.sucesso ? 'Autorizado' : 'Não autorizado'}
                  </td>
                  <td style={st.td}>{t.duracao_ms || '—'}</td>
                  <td style={{ ...st.td, color: C.muted }}>{t.erro || '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== Configuração =====================
const CONFIG_FIELDS = [
  { grupo: 'Ambiente e Endpoints', campos: [
    { k: 'ambiente', label: 'Ambiente', tipo: 'select', opcoes: ['homologacao', 'producao'] },
    { k: 'endpoint_base_hml', label: 'Endpoint homologação' },
    { k: 'endpoint_base_prod', label: 'Endpoint produção' },
    { k: 'endpoint_proxy', label: 'Proxy (tem precedência)', ph: 'ex.: https://dbprtn.../ipsemg/' },
  ]},
  { grupo: 'Credenciais do prestador (SAFe)', campos: [
    { k: 'login', label: 'Login' },
    { k: 'senha', label: 'Senha', tipo: 'password' },
    { k: 'codigo_prestador', label: 'Código do prestador (codigoPrestadorNaPolimed)' },
    { k: 'numero_ems', label: 'Número da operadora (numeroEms)' },
    { k: 'registro_ans', label: 'Registro ANS' },
    { k: 'terminal', label: 'Terminal' },
  ]},
  { grupo: 'Executante (padrão)', campos: [
    { k: 'exec_cbos', label: 'CBO-S executante (60 = Patologia Clínica)' },
    { k: 'exec_cons_sigla', label: 'Conselho (sigla)' },
    { k: 'exec_cons_uf', label: 'Conselho (UF)' },
  ]},
];

function TabConfig() {
  const [config, setConfig] = useState({});
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);

  const carregar = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/ipsemg/config`); setConfig(r.data || {}); }
    catch { setMsg({ kind: 'err', txt: 'Falha ao carregar configuração' }); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setSaving(true); setMsg(null);
    try { await axios.put(`${API_URL}/ipsemg/config`, config); await carregar(); setMsg({ kind: 'ok', txt: 'Configuração salva.' }); }
    catch (e) { setMsg({ kind: 'err', txt: e.response?.data?.error || 'Erro ao salvar' }); }
    finally { setSaving(false); }
  };

  const testar = async () => {
    setTesting(true); setTestRes(null);
    try { const r = await axios.post(`${API_URL}/ipsemg/config/testar-conexao`); setTestRes(r.data); }
    catch (e) { setTestRes({ sucesso: false, mensagem: e.response?.data?.detalhe || e.response?.data?.error || 'Erro no teste' }); }
    finally { setTesting(false); }
  };

  const set = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  return (
    <div style={st.card}>
      {msg && <div style={st.banner(msg.kind)}>{msg.txt}</div>}
      {config.ambiente === 'producao' && (
        <div style={st.banner('warn')}>
          Ambiente de <b>produção</b>: as consultas geram autorização real no IPSEMG.
        </div>
      )}
      {CONFIG_FIELDS.map(sec => (
        <div key={sec.grupo} style={{ marginBottom: 18 }}>
          <div style={st.sectionTitle}>{sec.grupo}</div>
          <div style={st.grid}>
            {sec.campos.map(f => (
              <div key={f.k}>
                <label style={st.label}>{f.label}</label>
                {f.tipo === 'select' ? (
                  <select style={st.input} value={config[f.k] || ''} onChange={e => set(f.k, e.target.value)}>
                    {f.opcoes.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input style={st.input} type={f.tipo === 'password' ? 'password' : 'text'}
                    placeholder={f.ph || ''} value={config[f.k] || ''} onChange={e => set(f.k, e.target.value)} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
        <button style={st.btn()} onClick={salvar} disabled={saving}>{saving ? 'Salvando…' : 'Salvar configuração'}</button>
        <button style={st.btn('ghost')} onClick={testar} disabled={testing}>{testing ? 'Testando…' : 'Testar conexão'}</button>
      </div>
      {testRes && (
        <div style={{ ...st.banner(testRes.sucesso ? 'ok' : 'err'), marginTop: 14 }}>
          {testRes.sucesso ? '✓ ' : '✗ '}{testRes.mensagem}
          {testRes.http_status ? ` · HTTP ${testRes.http_status}` : ''}{testRes.duracao_ms ? ` · ${testRes.duracao_ms}ms` : ''}
        </div>
      )}
    </div>
  );
}
